import { OkDialog } from '@/components/molecules';
import { OTPVerificationScreen } from '@/components/organisms/OTPVerificationScreen';
import { useTranslation } from '@/core/hooks';
import { logger } from '@/core/logger';
import { navigateToWelcomeAfterLogout } from '@/core/navigation/logoutNavigation';
import { deviceIdService } from '@/core/services';
import { useAppStore } from '@/core/stores/appStore';
import { registrationService } from '@/modules/onboarding/registration/services/registrationService';
import { RegistrationStorage } from '@/modules/onboarding/registration/storage/registrationStorage';
import { REGISTRATION_STEP, REGISTRATION_TYPE, useManualRegistrationStore } from '@/modules/onboarding/registration/store/registrationStore';
import type { RegisterErrorResponse, RegisterRequest, RegisterSuccessResponse } from '@/modules/onboarding/registration/types';
import { useRegistrationProgress } from '@/modules/onboarding/registration/utils/registrationProgress';
import { useOTP } from '@/modules/onboarding/verification/otp/hooks/useOTP';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';

export default function EmailOTPInline() {
    const params = useLocalSearchParams();
    const router = useRouter();
    const { t } = useTranslation();

    // Use central progress calculation instead of URL params
    const progress = useRegistrationProgress();
    const initialContactInfo = params.contactInfo as string | undefined;

    const [contactInfo, setContactInfo] = useState<string>(initialContactInfo || '');
    const [showOTP, setShowOTP] = useState<boolean>(!!initialContactInfo);
    const [isRegistering, setIsRegistering] = useState(false);
    const [registerError, setRegisterError] = useState<string | null>(null);
    const [alreadyRegisteredDialogVisible, setAlreadyRegisteredDialogVisible] = useState(false);
    const [noPropertyDialogVisible, setNoPropertyDialogVisible] = useState(false);

    // Get registration store
    const registrationStore = useManualRegistrationStore();
    const customerId = registrationStore.registrationResponse?.customer_id || null;
    const registrationId = registrationStore.registrationResponse?.registration_id || null;
    const setPhoneOtpVerified = registrationStore.setPhoneOtpVerified;
    const setEmailOtpVerified = registrationStore.setEmailOtpVerified;
    const setEmailOtpTransactionId = registrationStore.setEmailOtpTransactionId;
    const setStep = registrationStore.setStep;

    // Check if this is a registration flow (formData exists but registrationResponse is null)
    const isRegistrationFlow = !!registrationStore.formData && !registrationStore.registrationResponse;

    // Check if this is manual registration or UAE Pass registration (for non-registration flow)
    const uaePassRegistration = useAppStore((state) => state.uaePassRegistration);
    const manualRegistration = registrationStore.registrationResponse;
    const isManualRegistration = !!manualRegistration && !uaePassRegistration;

    const {
        transactionId,
        isLoading: isVerifyingOTP,
        error: otpError,
        isVerified,
        verifyOtp,
        resendOtp,
        clearError,
        sendOtp,
        resetVerification,
    } = useOTP({
        type: 'email',
        contactInfo,
        autoSend: false, // We'll send manually when contact is submitted
        isRegistration: isRegistrationFlow, // Use registration OTP endpoint
    });

    const [isSendingOTP, setIsSendingOTP] = useState(false);

    const handleContactSubmit = useCallback((contact: string) => {
        logger.info('EmailOTPInline', 'Email submitted, sending OTP', { contact, isRegistrationFlow });
        setContactInfo(contact);
        
        // For registration flow, update step to EMAIL_OTP immediately when email is submitted
        // This prevents splash screen from re-routing on app restart
        if (isRegistrationFlow) {
            setStep(REGISTRATION_STEP.EMAIL_OTP);
            logger.info('EmailOTPInline', 'Step updated to EMAIL_OTP for registration flow');
        }
        
        // Show OTP input immediately (don't wait for API response)
        setShowOTP(true);
        setIsSendingOTP(true);
        // Send OTP in background (don't await - let it run async)
        sendOtp(contact)
            .then(() => {
                logger.info('EmailOTPInline', 'OTP sent successfully');
            })
            .catch((error) => {
                logger.error('EmailOTPInline', 'Failed to send OTP', error);
            })
            .finally(() => {
                setIsSendingOTP(false);
            });
    }, [sendOtp, isRegistrationFlow, setStep]);

    const handleOTPComplete = useCallback(async (otp: string) => {
        logger.info('EmailOTPInline', 'Email OTP entered, verifying...', { otp, isRegistrationFlow });
        const verified = await verifyOtp(otp);
        
        if (verified && isRegistrationFlow) {
            // For registration flow, call register API after OTP verification
            logger.info('EmailOTPInline', 'Email OTP verified successfully (registration flow), calling register API');
            
            try {
                setIsRegistering(true);
                setRegisterError(null);
                
                const formData = registrationStore.formData;
                const registrationType = registrationStore.registrationType;
                
                if (!formData || !registrationType) {
                    throw new Error('Form data or registration type is missing');
                }
                
                // Get device ID
                const deviceId = await deviceIdService.getDeviceId();
                
                // Get transaction IDs from state and hook
                const phoneOtpTxnId = registrationStore.phoneOtpTransactionId || '';
                const emailOtpTxnId = transactionId || '';
                
                // Store email OTP transaction ID in state
                if (emailOtpTxnId) {
                    setEmailOtpTransactionId(emailOtpTxnId);
                    logger.info('EmailOTPInline', 'Email OTP transaction ID stored', { emailOtpTxnId });
                }
                
                if (!phoneOtpTxnId || !emailOtpTxnId) {
                    const errorMsg = 'Unable to complete registration. Please verify both your phone and email OTP codes again.';
                    logger.error('EmailOTPInline', 'OTP transaction IDs are missing', { 
                        hasPhoneTxnId: !!phoneOtpTxnId, 
                        hasEmailTxnId: !!emailOtpTxnId 
                    });
                    setRegisterError(errorMsg);
                    setIsRegistering(false);
                    return false;
                }
                
                // Prepare register API request with formData + verified phone + verified email + transaction IDs
                const apiRequest: RegisterRequest = {
                    first_name: formData.firstName,
                    middle_name: formData.middleName || '',
                    last_name: formData.lastName,
                    ...(registrationType === REGISTRATION_TYPE.EMIRATES_ID && { emirates_id: formData.emiratesId }),
                    passport_number: formData.passportNumber,
                    nationality: formData.nationality,
                    registration_type: 'Manual' as const,
                    device_id: deviceId,
                    country_code: formData.country_code || '971', // From phone OTP verification
                    phone_number: formData.phone || '', // From phone OTP verification
                    email: contactInfo, // Verified email
                    ...(formData.tnc_version !== undefined && { tnc_version: String(formData.tnc_version) }), // TNC version if available (explicitly convert to string)
                    otpEmailTxnId: emailOtpTxnId, // Email OTP transaction ID (mandatory)
                    otpSMSTxnId: phoneOtpTxnId, // Phone/SMS OTP transaction ID (mandatory)
                };
                
                logger.info('EmailOTPInline', 'Calling register API', { registrationType });
                
                // Call registration API
                const response = await registrationService.register(apiRequest);
                
                // Check if response is an error response (has 'code' and 'message', not 'details')
                if ('code' in response && 'message' in response) {
                    const errorResponse = response as RegisterErrorResponse;
                    logger.warn('EmailOTPInline', 'Registration API returned error', { 
                        code: errorResponse.code, 
                        message: errorResponse.message 
                    });
                    
                    // Check if already registered (error response)
                    if (errorResponse.message === 'You are already registered' || 
                        errorResponse.message.toLowerCase().includes('already registered')) {
                        logger.info('EmailOTPInline', 'User already registered (error response)');
                        setAlreadyRegisteredDialogVisible(true);
                        return false; // Return false to prevent navigation
                    }
                    
                    // Show error dialog for other errors
                    const errorMessage = errorResponse.message || 'Registration failed. Please try again.';
                    setRegisterError(errorMessage);
                    return false; // Return false to prevent navigation
                }
                
                // Handle success response
                if ('details' in response && response.status === 'success') {
                    const successResponse = response as RegisterSuccessResponse;
                    
                    // Check if already registered (success response with that message - should not happen but handle it)
                    if (successResponse.details.message === 'You are already registered' || 
                        successResponse.details.message.toLowerCase().includes('already registered')) {
                        logger.info('EmailOTPInline', 'User already registered (success response with message)');
                        setAlreadyRegisteredDialogVisible(true);
                        return false; // Return false to prevent navigation
                    }
                    
                    // Check if customer_id is null or empty (even for new registration)
                    if (!successResponse.details.customer_id || successResponse.details.customer_id.trim() === '') {
                        logger.warn('EmailOTPInline', 'Registration successful but customer_id is null or empty');
                        setNoPropertyDialogVisible(true);
                        return false; // Return false to prevent navigation
                    }
                    
                    // Store email OTP verification status
                    setEmailOtpVerified(true);
                    // Save verified email to registration store
                    registrationStore.setVerifiedContact('email', contactInfo);
                    
                    // Store registration response
                    registrationStore.setRegistrationResponse({
                        customer_id: successResponse.details.customer_id,
                        registration_id: successResponse.details.registration_id,
                        documents: successResponse.details.documents,
                    });
                    
                    // Store registration IDs for status API calls
                    try {
                        const registrationIds: {
                            registrationId: string;
                            customerId: string;
                            idn: string;
                            passportNumber?: string;
                            nationality: string;
                        } = {
                            registrationId: successResponse.details.registration_id,
                            customerId: successResponse.details.customer_id,
                            idn: registrationType === REGISTRATION_TYPE.EMIRATES_ID && formData.emiratesId
                                ? formData.emiratesId.replace(/-/g, '') // Remove dashes for storage
                                : '', // Empty for passport-only flow
                            nationality: formData.nationality,
                        };
                        
                        // Only include passportNumber if it exists (for passport-only flow)
                        if (registrationType === REGISTRATION_TYPE.PASSPORT && formData.passportNumber) {
                            registrationIds.passportNumber = formData.passportNumber.trim().toUpperCase();
                        }
                        
                        await RegistrationStorage.setRegistrationIds(registrationIds);
                        logger.info('EmailOTPInline', 'Registration IDs stored', {
                            hasIdn: registrationType === REGISTRATION_TYPE.EMIRATES_ID && !!formData.emiratesId,
                            hasPassportNumber: registrationType === REGISTRATION_TYPE.PASSPORT && !!formData.passportNumber,
                            nationality: formData.nationality,
                        });
                    } catch (error) {
                        logger.error('EmailOTPInline', 'Failed to store registration IDs', error);
                        // Don't block navigation if storage fails
                    }
                    
                    // Update step to document-capture
                    setStep(REGISTRATION_STEP.DOCUMENT_CAPTURE);
                    
                    logger.info('EmailOTPInline', 'Registration successful', {
                        customerId: successResponse.details.customer_id,
                        registrationId: successResponse.details.registration_id,
                    });
                    
                    // Navigate to document upload
                    router.push('/document-upload' as any);
                    return true;
                }
            } catch (error: any) {
                logger.error('EmailOTPInline', 'Registration failed', error);
                
                // Check if error response indicates "already registered"
                const errorMessage = error?.response?.data?.message || error?.message || '';
                if (errorMessage === 'You are already registered' || 
                    errorMessage.toLowerCase().includes('already registered')) {
                    logger.info('EmailOTPInline', 'User already registered (HTTP error)');
                    setAlreadyRegisteredDialogVisible(true);
                    return false; // Return false to prevent navigation
                }
                
                // Handle validation errors from API
                const apiErrors = error?.response?.data?.errors || error?.response?.errors;
                if (apiErrors && Array.isArray(apiErrors) && apiErrors.length > 0) {
                    const validationErrorMessage = apiErrors[0]?.message || 'Registration failed. Please try again.';
                    setRegisterError(validationErrorMessage);
                } else {
                    const genericErrorMessage = errorMessage || 'Registration failed. Please try again.';
                    setRegisterError(genericErrorMessage);
                }
                return false; // Return false to prevent navigation
            } finally {
                setIsRegistering(false);
            }
        }
        
        return verified; // Return verification result for OTPVerificationScreen to handle verify-details (non-registration flow)
    }, [verifyOtp, isRegistrationFlow, contactInfo, registrationStore, setEmailOtpVerified, setEmailOtpTransactionId, setStep, router, transactionId]);

    const handleVerifyDetailsSuccess = useCallback(async (_type: 'phone' | 'email', contact: string) => {
        // This is only called for non-registration flows (when customerId and registrationId exist)
        logger.info('EmailOTPInline', 'Verify-details API succeeded, marking registration as complete');
        // Clear phone OTP verification status since both OTPs are now complete
        setPhoneOtpVerified(false);
        // Save verified email to registration store
        registrationStore.setVerifiedContact('email', contact);

        // Navigate based on registration type
        // Manual registration: go to document-review (use push so back button works)
        // UAE Pass registration: go directly to registration-success
        if (isManualRegistration) {
            logger.info('EmailOTPInline', 'Manual registration detected, navigating to document-review');
            router.push('/document-review');
        } else {
            logger.info('EmailOTPInline', 'UAE Pass registration detected, navigating to registration-success');
            // Mark registration success so splash screen routes correctly on restart
            try {
                await RegistrationStorage.setReachedRegistrationSuccess();
                logger.info('EmailOTPInline', 'Registration success state saved');
            } catch (error) {
                logger.error('EmailOTPInline', 'Failed to save registration success state', error);
            }
            router.replace('/registration-success' as any);
        }
    }, [router, setPhoneOtpVerified, isManualRegistration, registrationStore]);

    const handleResendOTP = useCallback(async () => {
        logger.info('EmailOTPInline', 'Resend OTP requested');
        clearError();
        await resendOtp();
    }, [resendOtp, clearError]);

    const handleEditContact = useCallback(() => {
        // Reset showOTP when edit is clicked
        setShowOTP(false);
        // Reset OTP verification state (success/failure)
        resetVerification();
        // Also clear registration error if any
        setRegisterError(null);
    }, [resetVerification]);

    // Show registration error if any
    const displayError = registerError || otpError;

    return (
        <>
            <OTPVerificationScreen
                type="email"
                mode="inline"
                progress={progress}
                contactInfo={contactInfo}
                showOTP={showOTP}
                loading={isSendingOTP || isRegistering}
                verifyingOTP={isVerifyingOTP || isRegistering}
                otpError={displayError}
                otpSuccess={isVerified && !isRegistering}
                onContactSubmit={handleContactSubmit}
                onEditContact={handleEditContact}
                onOTPComplete={handleOTPComplete}
                onResendOTP={handleResendOTP}
                onOTPChange={() => {
                    clearError();
                    setRegisterError(null);
                }}
                {...(customerId && registrationId && !isRegistrationFlow
                    ? {
                        verifyDetailsConfig: {
                            customerId,
                            registrationId,
                        },
                        onVerifyDetailsSuccess: handleVerifyDetailsSuccess,
                    }
                    : {})}
            />
            {/* Already Registered Dialog */}
            <OkDialog
                visible={alreadyRegisteredDialogVisible}
                message={t('registration.alreadyRegistered')}
                okText={t('auth.login')}
                onOk={async () => {
                    setAlreadyRegisteredDialogVisible(false);
                    registrationStore.clear();
                    try {
                        await navigateToWelcomeAfterLogout();
                    } catch (error) {
                        logger.error('EmailOTPInline', 'Failed to navigate after already-registered', error);
                    }
                }}
            />
            {/* Registration cannot be processed dialog */}
            <OkDialog
                visible={noPropertyDialogVisible}
                message={t('registration.registrationCannotBeProcessed')}
                onOk={async () => {
                    setNoPropertyDialogVisible(false);
                    
                    try {
                        logger.info('EmailOTPInline', 'Clearing all registration data and navigating to welcome');
                        
                        // Clear all registration store
                        await useManualRegistrationStore.getState().clearAll();
                        
                        // Clear UAE Pass registration
                        useAppStore.getState().clearUaePassRegistration();
                        
                        // Clear all registration storage
                        await RegistrationStorage.clearAll();
                        
                        logger.info('EmailOTPInline', 'All registration data cleared, navigating to welcome');
                        
                        // Clear navigation stack and navigate to welcome
                        try {
                            if (router.canGoBack()) {
                                router.dismissAll();
                            }
                        } catch (error) {
                            logger.debug('EmailOTPInline', 'dismissAll failed, using replace only', error);
                        }
                        router.replace('/welcome');
                    } catch (error) {
                        logger.error('EmailOTPInline', 'Failed to clear registration data', error);
                        // Still navigate to welcome even if clearing fails
                        try {
                            if (router.canGoBack()) {
                                router.dismissAll();
                            }
                        } catch (navError) {
                            logger.debug('EmailOTPInline', 'dismissAll failed, using replace only', navError);
                        }
                        router.replace('/welcome');
                    }
                }}
            />
        </>
    );
}
